/* eslint-disable no-prototype-builtins */
import { APP_WILL_MOUNT } from '../app/actionTypes';
import { getJwtName } from '../jwt/functions';
import MiddlewareRegistry from '../redux/MiddlewareRegistry';

import { updateSettings } from './actions';
import escape from 'lodash/escape';

import { SET_LOCATION_URL } from '../connection/actionTypes';
import { participantUpdated } from '../participants/actions';
import { getLocalParticipant } from '../participants/functions';
import { parseURLParams } from '../util/parseURLParams';

import { SETTINGS_UPDATED } from './actionTypes';

/**
 * The middleware of the feature base/settings. Distributes changes to the state
 * of base/settings to the states of other features computed from the state of
 * base/settings.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register((store) => (next) => (action) => {
  const result = next(action);

  switch (action.type) {
    case SETTINGS_UPDATED:
      _updateLocalParticipant(store, action);
      break;
    case SET_LOCATION_URL:
      _updateLocalParticipantFromUrl(store);
      break;
  }

  return result;
});

/**
 * Maps the settings field names to participant names where they don't match.
 * Currently there is only one such field, but may be extended in the future.
 *
 * @private
 * @param {string} settingsField - The name of the settings field to map.
 * @returns {string}
 */
function _mapSettingsFieldToParticipant(settingsField) {
  switch (settingsField) {
    case 'displayName':
      return 'name';
  }

  return settingsField;
}

/**
 * Updates the local participant according to settings changes.
 *
 * @param {Store} store - The redux store.
 * @param {Object} action - The dispatched action.
 * @private
 * @returns {void}
 */
function _updateLocalParticipant({ dispatch, getState }, action) {
  const { settings } = action;
  const localParticipant = getLocalParticipant(getState());
  const newLocalParticipant = {
    ...localParticipant
  };

  for (const key in settings) {
    if (settings.hasOwnProperty(key)) {
      newLocalParticipant[_mapSettingsFieldToParticipant(key)] = settings[key];
    }
  }

  dispatch(
    participantUpdated({
      ...newLocalParticipant,
      id: newLocalParticipant.id ?? ''
    })
  );
}

/**
 * Returns the userInfo set in the URL.
 *
 * @param {Store} store - The redux store.
 * @private
 * @returns {void}
 */
function _updateLocalParticipantFromUrl({ dispatch, getState }) {
  const urlParams = parseURLParams(
    getState()['features/base/connection'].locationURL ?? ''
  );
  const urlEmail = urlParams['userInfo.email'];
  const urlDisplayName = urlParams['userInfo.displayName'];

  if (!urlEmail && !urlDisplayName) {
    return;
  }

  const localParticipant = getLocalParticipant(getState());

  if (localParticipant) {
    const displayName = escape(urlDisplayName);
    const email = escape(urlEmail);

    dispatch(
      participantUpdated({
        ...localParticipant,
        email,
        name: displayName
      })
    );

    dispatch(
      updateSettings({
        displayName,
        email
      })
    );
  }
}

/**
 * The middleware of the feature base/settings. Distributes changes to the state
 * of base/settings to the states of other features computed from the state of
 * base/settings.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register((store) => (next) => (action) => {
  const result = next(action);

  switch (action.type) {
    case APP_WILL_MOUNT:
      _initializeShowPrejoin(store);
      // break;
      // case PREJOIN_INITIALIZED:
      _maybeUpdateDisplayName(store);
      break;
  }

  return result;
});

/**
 * Overwrites the showPrejoin flag based on cached used selection for showing prejoin screen.
 *
 * @param {Store} store - The redux store.
 * @private
 * @returns {void}
 */
function _initializeShowPrejoin() {
  // const { userSelectedSkipPrejoin } = getState()['features/base/settings'];
  // if (userSelectedSkipPrejoin) {
  //     dispatch(setPrejoinPageVisibility(false));
  // }
}

/**
 * Updates the display name to the one in JWT if there is one.
 *
 * @param {Store} store - The redux store.
 * @private
 * @returns {void}
 */
function _maybeUpdateDisplayName({ dispatch, getState }) {
  const state = getState();
  const hasJwt = Boolean(state['features/base/jwt'].jwt);

  if (hasJwt) {
    const displayName = getJwtName(state);

    if (displayName) {
      dispatch(
        updateSettings({
          displayName
        })
      );
    }
  }
}
